import type { AssignmentApp } from "@/lib/data/requests/matters/matter-task";
import { useAppMentionOptions } from "@/hooks/use-app-mention-options";
import type { DirectoryUser } from "@/lib/types";
import { type NotesWithType } from "@/components/features/matter/notes/matter-note-columns";
import {
  ActionConfirmationModal,
  Badge,
  Button,
  Card,
  Checkbox,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Text,
  TextWithMentions,
} from "@/components/ui";
import { CreatedByUserText } from "@/components/ui/CreatedByUserText/CreatedByUserText";
import {
  MatterBreadcrumb,
  type MatterBreadcrumbProps,
} from "@/components/ui/MatterBreadcrumb.tsx/MatterBreadcrumb";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/shadcn/Accordion/Accordion";
import { UserProfilePhoto } from "@/components/ui/UserProfilePhoto/UserProfilePhoto";
import { useUser } from "@/components/UserProvider";
import { useGetSubitemsForBreadcrumbs } from "@/hooks/use-get-subitems-for-breadcrumbs";
import { useTiptapMentions } from "@/hooks/useTiptapMentions";
import { getUsersOptions } from "@/lib/data/react-query";
import {
  useDeleteMatterNote,
  useUpdateMatterNote,
} from "@/lib/data/react-query/matters/matter-note";
import {
  useCreateMatterNoteComment,
  useDeleteMatterNoteComment,
  useUpdateMatterNoteComment,
} from "@/lib/data/react-query/matters/matter-note-comment";
import {
  ACTION_PERMISSION_KEYS,
  ACTION_PERMISSION_VALUES,
} from "@/lib/data/static/permissionOptions";
import type { Matter, MatterNoteComment } from "@/lib/types";
import { cn } from "@/lib/utils/cn";
import { convertToLocalFromDatabaseDateTime } from "@/lib/utils/dateUtils";
import { formatDateTime, formatUsersName } from "@/lib/utils/format";
import { convertBackendMessageToDisplay } from "@/lib/utils/mentionUtils";
import { useQuery } from "@tanstack/react-query";
import { EditorContent } from "@tiptap/react";
import dayjs from "dayjs";
import { MoreVertical, Pencil, Pin, PinOff, Trash2 } from "lucide-react";
import { useEffect, useId, useMemo, useRef, useState } from "react";
import { MatterNoteContextMenu } from "./MatterNoteContextMenu";

const COMMENT_MAX_LINES = 4;

const MatterNoteCommentText = ({
  text,
  users,
  mentionedApps,
}: {
  text: string;
  users: DirectoryUser[];
  mentionedApps?: AssignmentApp[];
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showButton, setShowButton] = useState(false);
  const displayRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (measureRef.current && displayRef.current) {
      const measureElement = measureRef.current;
      const displayElement = displayRef.current;
      const lineHeight = parseInt(
        window.getComputedStyle(displayElement).lineHeight,
      );
      const maxHeight = lineHeight * COMMENT_MAX_LINES;
      const naturalHeight = measureElement.scrollHeight;

      setShowButton(naturalHeight > maxHeight);
    }
  }, [text]);

  return (
    <div>
      <div
        ref={measureRef}
        className="invisible absolute -top-9999 left-0"
        style={{
          position: "absolute",
          top: "-9999px",
          left: "-9999px",
          visibility: "hidden",
          width: "100%",
        }}
      >
        <Text variant="body" className="whitespace-pre-wrap">
          {text}
        </Text>
      </div>
      <div ref={displayRef}>
        <Text
          variant="body"
          className={cn(
            "whitespace-pre-wrap",
            isExpanded ? "" : "line-clamp-4",
          )}
        >
          <TextWithMentions
            text={text}
            users={users}
            mentionedApps={mentionedApps}
          />
        </Text>
      </div>
      {showButton && (
        <button
          type="button"
          onClick={() => {
            setIsExpanded(!isExpanded);
          }}
          className="mt-2"
        >
          <Text variant="body" color="primary" className="hover:underline">
            {isExpanded ? "Show less" : "Show more"}
          </Text>
        </button>
      )}
    </div>
  );
};

interface MatterNoteProps {
  note: NotesWithType & { comments?: MatterNoteComment[] };
  matter: Matter;
  massTortId?: string;
  textClassName?: string;
  disableBreadcrumbs?: boolean;
  variant?: "default" | "eventlog";
  highlightPinned?: boolean;
  showComments?: boolean;
  commentsMode?: "default" | "readOnly";
}

export const MatterNote = ({
  note,
  matter,
  massTortId,
  textClassName,
  disableBreadcrumbs = false,
  variant = "default",
  highlightPinned = true,
  showComments,
  commentsMode = "default",
}: MatterNoteProps) => {
  const { currentUser, hasActionPermission } = useUser();
  const getSubitemsForBreadcrumbs = useGetSubitemsForBreadcrumbs();
  const annotationParentPayload = massTortId
    ? { massTortId }
    : { matterId: matter.id };
  const annotationParentPermissionKey = massTortId
    ? ACTION_PERMISSION_KEYS.massTort
    : ACTION_PERMISSION_KEYS.matter;

  const updateMatterNote = useUpdateMatterNote();
  const deleteMatterNote = useDeleteMatterNote();
  const createMatterNoteComment = useCreateMatterNoteComment();
  const updateMatterNoteComment = useUpdateMatterNoteComment();
  const deleteMatterNoteComment = useDeleteMatterNoteComment();

  const [isDeleting, setIsDeleting] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [noteDraft, setNoteDraft] = useState(note.message);
  const [isDraft, setIsDraft] = useState(Boolean(note.draft));
  const [pinnedDraft, setPinnedDraft] = useState(Boolean(note.pinned));
  const [commentInput, setCommentInput] = useState("");
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
  const [commentEditInput, setCommentEditInput] = useState("");
  const [commentToDelete, setCommentToDelete] =
    useState<MatterNoteComment | null>(null);
  const lastSyncedCommentEditRef = useRef<{
    commentId: string;
    message: string;
  } | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [hasOverflow, setHasOverflow] = useState(false);
  const textContentRef = useRef<HTMLDivElement | null>(null);
  const draftCheckboxId = useId();

  const { data: usersResponse, isLoading: isLoadingUsers } =
    useQuery(getUsersOptions());
  const users = useMemo(() => usersResponse?.data ?? [], [usersResponse]);
  const noteUser = users.find((user) => user.id === note.userId);
  const noteAuthor = note.integrationAppId
    ? {
        id: note.integrationAppId,
        firstName: note.integrationAppName?.trim() || "Integration app",
        lastName: "",
      }
    : noteUser;
  const noteAuthorLabel = note.integrationAppId
    ? note.integrationAppName?.trim() || "Integration app"
    : formatUsersName(noteUser);
  const creationDate = dayjs(
    convertToLocalFromDatabaseDateTime(note.dateCreated),
  );
  const creationInfo = `Created by ${noteAuthorLabel || "Unknown user"} on ${creationDate.isValid() ? creationDate.format("MMM D, YYYY [@] h:mm A") : "Unknown date"}`;
  const mentionOptions = useAppMentionOptions(users, !massTortId, [
    ...(note.mentionedApps ?? []),
    ...(note.comments ?? matter.noteComments)
      .filter((comment) => comment.noteId === note.id)
      .flatMap((comment) => comment.mentionedApps ?? []),
  ]);

  const displayMessage = useMemo(() => {
    if (!note.message) {
      return "";
    }

    if (mentionOptions.length === 0) {
      return note.message;
    }

    return convertBackendMessageToDisplay(note.message, mentionOptions);
  }, [note.message, mentionOptions]);

  const isCommentsReadOnly = commentsMode === "readOnly";

  const hasUpdatePermission = hasActionPermission(
    annotationParentPermissionKey,
    ACTION_PERMISSION_VALUES.update,
  );
  const hasDeletePermission = hasActionPermission(
    annotationParentPermissionKey,
    ACTION_PERMISSION_VALUES.update,
  );
  const hasCreateCommentPermission = hasActionPermission(
    annotationParentPermissionKey,
    ACTION_PERMISSION_VALUES.update,
  );
  const canCreateComment = hasCreateCommentPermission && !isCommentsReadOnly;
  const isNoteOwner = currentUser.id === note.userId;
  const canEditNote = hasUpdatePermission || isNoteOwner;
  const canDeleteNote = hasDeletePermission || isNoteOwner;

  const { editor, getBackendMessage, setContent } = useTiptapMentions({
    plainText: true,
    mentionOptions,
    isLoading: isLoadingUsers,
    onUpdate: (value) => {
      setNoteDraft(value);
    },
    initialContent: note.message,
    isEditable: isEditing && canEditNote,
    className:
      "min-w-full max-h-[260px] min-h-[140px] overflow-y-auto border-0 bg-transparent px-4 py-3 text-sm shadow-none focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none",
    placeholder: "Update note...",
  });
  const {
    editor: commentEditor,
    getBackendMessage: getCommentBackendMessage,
    setContent: setCommentContent,
    mentionDropdownOpenRef: commentMentionDropdownOpenRef,
  } = useTiptapMentions({
    mentionOptions,
    isLoading: isLoadingUsers,
    onUpdate: (value) => {
      setCommentInput(value);
    },
    isEditable: canCreateComment,
    className:
      "bg-background max-h-[96px] border-0 px-4 py-3 text-sm shadow-none focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none",
    placeholder: "Add a comment...",
  });
  const {
    editor: editCommentEditor,
    getBackendMessage: getEditCommentBackendMessage,
    setContent: setEditCommentContent,
    mentionDropdownOpenRef: editCommentMentionDropdownOpenRef,
  } = useTiptapMentions({
    mentionOptions,
    isLoading: isLoadingUsers,
    onUpdate: (value) => {
      setCommentEditInput(value);
    },
    isEditable: Boolean(editingCommentId) && !isCommentsReadOnly,
    className:
      "bg-background max-h-[96px] border-0 px-4 py-3 text-sm shadow-none focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none",
    placeholder: "Update comment...",
  });

  const lastSyncedViewRef = useRef<{
    noteId: string;
    message: string;
    pinned: boolean;
  } | null>(null);

  useEffect(() => {
    if (isEditing || !editor) return;

    const nextSnapshot = {
      noteId: note.id,
      message: displayMessage,
      pinned: Boolean(note.pinned),
    };

    const lastSnapshot = lastSyncedViewRef.current;
    const hasChanges =
      !lastSnapshot ||
      lastSnapshot.noteId !== nextSnapshot.noteId ||
      lastSnapshot.message !== nextSnapshot.message ||
      lastSnapshot.pinned !== nextSnapshot.pinned;

    if (!hasChanges) {
      return;
    }

    lastSyncedViewRef.current = nextSnapshot;
    setNoteDraft(displayMessage);
    setPinnedDraft(Boolean(note.pinned));
    setIsDraft(Boolean(note.draft));
    setContent(note.message);
  }, [
    displayMessage,
    note.message,
    note.id,
    note.pinned,
    note.draft,
    isEditing,
    setContent,
    editor,
  ]);

  const lastSyncedEditRef = useRef<string | null>(null);
  useEffect(() => {
    if (!isEditing) {
      lastSyncedEditRef.current = null;
      return;
    }

    if (lastSyncedEditRef.current === displayMessage) {
      return;
    }

    lastSyncedEditRef.current = displayMessage;
    setContent(note.message);
  }, [displayMessage, note.message, isEditing, setContent]);

  const isPinned = isEditing ? pinnedDraft : note.pinned;
  const isDraftNote = isEditing ? isDraft : note.draft;
  const trimmedDraft = noteDraft.trim();
  const hasChanges =
    getBackendMessage().trim() !== note.message.trim() ||
    pinnedDraft !== Boolean(note.pinned) ||
    isDraft !== Boolean(note.draft);
  const canSave =
    canEditNote &&
    trimmedDraft.length > 0 &&
    hasChanges &&
    Boolean(editor) &&
    !updateMatterNote.isPending;
  const noteComments = useMemo(() => {
    const fallbackComments = note.comments ?? matter.noteComments;
    const getTimestamp = (value?: string) =>
      value ? dayjs(value).valueOf() : 0;
    return fallbackComments
      .filter((comment) => comment.noteId === note.id)
      .sort(
        (a, b) => getTimestamp(a.dateCreated) - getTimestamp(b.dateCreated),
      );
  }, [matter.noteComments, note]);
  const commentCount = noteComments.length;
  const shouldRenderComments =
    typeof showComments === "boolean" ? showComments : variant !== "eventlog";

  const previousEditingCommentIdRef = useRef<string | null>(null);
  useEffect(() => {
    const previousEditingCommentId = previousEditingCommentIdRef.current;
    previousEditingCommentIdRef.current = editingCommentId;

    if (!editingCommentId) {
      if (!previousEditingCommentId) {
        return;
      }

      setCommentEditInput("");
      setEditCommentContent("");
      lastSyncedCommentEditRef.current = null;
      return;
    }

    const activeComment = noteComments.find(
      (comment) => comment.id === editingCommentId,
    );

    if (!activeComment) {
      setEditingCommentId(null);
      return;
    }

    const lastSnapshot = lastSyncedCommentEditRef.current;
    const nextSnapshot = {
      commentId: activeComment.id,
      message: activeComment.message,
    };

    if (
      lastSnapshot &&
      lastSnapshot.commentId === nextSnapshot.commentId &&
      lastSnapshot.message === nextSnapshot.message
    ) {
      return;
    }

    lastSyncedCommentEditRef.current = nextSnapshot;
    setCommentEditInput(activeComment.message);
    setEditCommentContent(activeComment.message);
  }, [
    editingCommentId,
    noteComments,
    setCommentEditInput,
    setEditCommentContent,
    setEditingCommentId,
  ]);

  const handleNoteStateToggle = async (state: "draft" | "pinned") => {
    if (updateMatterNote.isPending) {
      return;
    }

    await updateMatterNote.mutateAsync({
      id: note.id,
      ...annotationParentPayload,
      [state]: !note[state],
      dateModified: note.dateModified,
    });
  };

  const handlePinToggle = () => handleNoteStateToggle("pinned");
  const handleDraftToggle = () => handleNoteStateToggle("draft");

  const handleDelete = async () => {
    if (!canDeleteNote) {
      return;
    }
    setIsEditing(false);
    try {
      await deleteMatterNote.mutateAsync({
        id: note.id,
        ...annotationParentPayload,
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleStartEdit = () => {
    if (!canEditNote) {
      return;
    }
    setIsEditing(true);
    setNoteDraft(displayMessage);
    setPinnedDraft(Boolean(note.pinned));
    setIsDraft(Boolean(note.draft));
    lastSyncedEditRef.current = displayMessage;
    setContent(note.message);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setNoteDraft(displayMessage);
    setPinnedDraft(Boolean(note.pinned));
    setIsDraft(Boolean(note.draft));
    lastSyncedEditRef.current = null;
    setContent(note.message);
  };

  const handleSave = () => {
    if (!canSave) return;
    try {
      updateMatterNote.mutate({
        ...annotationParentPayload,
        id: note.id,
        message: getBackendMessage(),
        pinned: pinnedDraft,
        draft: isDraft,
        dateModified: note.dateModified,
      });
      setIsEditing(false);
    } catch {
      // Errors handled via mutation toast
    }
  };
  const canSubmitComment =
    canCreateComment &&
    Boolean(commentInput.trim()) &&
    !createMatterNoteComment.isPending;

  const handleAddComment = async () => {
    if (!canSubmitComment) {
      return;
    }

    const previousCommentInput = commentInput;
    const backendMessage = getCommentBackendMessage();

    setCommentContent("");
    setCommentInput("");

    try {
      await createMatterNoteComment.mutateAsync({
        ...annotationParentPayload,
        noteId: note.id,
        message: backendMessage,
        userId: currentUser.id,
      });
    } catch {
      setCommentContent(backendMessage);
      setCommentInput(previousCommentInput);
    }
  };

  const handleStartEditComment = (comment: MatterNoteComment) => {
    if (
      isCommentsReadOnly ||
      !(hasUpdatePermission || comment.userId === currentUser.id)
    ) {
      return;
    }

    setEditingCommentId(comment.id);
    setCommentEditInput(comment.message);
    setEditCommentContent(comment.message);
  };

  const handleCancelEditComment = () => {
    setEditingCommentId(null);
    setCommentEditInput("");
    setEditCommentContent("");
    lastSyncedCommentEditRef.current = null;
  };

  const handleSaveComment = async () => {
    if (isCommentsReadOnly || !editingCommentId || !commentEditInput.trim()) {
      return;
    }

    const activeComment = noteComments.find(
      (comment) => comment.id === editingCommentId,
    );

    if (
      !activeComment ||
      !(hasUpdatePermission || activeComment.userId === currentUser.id)
    ) {
      setEditingCommentId(null);
      return;
    }

    try {
      await updateMatterNoteComment.mutateAsync({
        ...annotationParentPayload,
        noteId: note.id,
        id: activeComment.id,
        message: getEditCommentBackendMessage(),
        dateModified: activeComment.dateModified,
      });
      setEditingCommentId(null);
      setCommentEditInput("");
      setEditCommentContent("");
      lastSyncedCommentEditRef.current = null;
    } catch {
      // errors handled in mutation
    }
  };

  const handleDeleteComment = async () => {
    if (!commentToDelete || isCommentsReadOnly) {
      return;
    }

    try {
      await deleteMatterNoteComment.mutateAsync({
        ...annotationParentPayload,
        noteId: note.id,
        id: commentToDelete.id,
      });

      if (editingCommentId === commentToDelete.id) {
        setEditingCommentId(null);
      }
    } finally {
      setCommentToDelete(null);
    }
  };

  const breadcrumbItems: MatterBreadcrumbProps["items"] = [
    {
      id: "matter",
      type: "Matter",
    },
  ];

  if (note.type !== "Matter") {
    breadcrumbItems.push(
      ...getSubitemsForBreadcrumbs(note, disableBreadcrumbs),
    );
  }

  // Only show message after users data has loaded to prevent flash of unconverted content
  const shouldShowMessage = !isLoadingUsers && note.message;
  useEffect(() => {
    setIsExpanded(false);
  }, [note.id]);

  useEffect(() => {
    if (!shouldShowMessage || isEditing) {
      setHasOverflow(false);
      return;
    }

    const element = textContentRef.current;
    if (!element) {
      setHasOverflow(false);
      return;
    }

    const determineOverflow = () => {
      if (isExpanded) {
        setHasOverflow(true);
        return;
      }
      const tolerancePx = 2;
      setHasOverflow(element.scrollHeight - element.clientHeight > tolerancePx);
    };

    determineOverflow();

    if (typeof ResizeObserver === "undefined") {
      return;
    }

    const resizeObserver = new ResizeObserver(() => {
      if (!isExpanded) {
        determineOverflow();
      }
    });
    resizeObserver.observe(element);
    return () => {
      resizeObserver.disconnect();
    };
  }, [shouldShowMessage, note.message, isExpanded, isEditing]);

  const commentsSection = shouldRenderComments ? (
    <Accordion
      type="single"
      collapsible
      className={cn(
        "border-border/60 border-t pt-2",
        variant === "eventlog" && "mt-4",
      )}
    >
      <AccordionItem value="note-comments" className="border-b-0">
        <AccordionTrigger className="cursor-pointer px-0 py-2 hover:no-underline">
          <div className="flex w-full items-center justify-between">
            <Text variant="subHeading">Comments</Text>
            <Text
              variant="caption"
              as="span"
              className="bg-muted rounded-full px-2 py-0.5 font-medium"
            >
              {commentCount}
            </Text>
          </div>
        </AccordionTrigger>
        <AccordionContent className="flex flex-col gap-3 px-0">
          {commentCount === 0 && (
            <Text variant="subHeading" data-testid="note-comments-empty">
              No comments yet.
            </Text>
          )}
          {commentCount > 0 && !isLoadingUsers && (
            <div className="flex flex-col gap-3">
              {noteComments.map((comment) => {
                const isEditingComment = editingCommentId === comment.id;
                const commentUser = users.find(
                  (user) => user.id === comment.userId,
                );
                const isCommentOwner = comment.userId === currentUser.id;
                const canEditComment =
                  !isCommentsReadOnly &&
                  (hasUpdatePermission || isCommentOwner);
                const canDeleteComment =
                  !isCommentsReadOnly &&
                  (hasDeletePermission || isCommentOwner);
                const showCommentActions =
                  !isEditingComment && (canEditComment || canDeleteComment);
                const canSaveEditedComment =
                  !isCommentsReadOnly &&
                  isEditingComment &&
                  Boolean(commentEditInput.trim()) &&
                  (hasUpdatePermission || isCommentOwner) &&
                  !updateMatterNoteComment.isPending;

                return (
                  <article
                    key={comment.id}
                    className="border-border bg-card/80 relative flex gap-3 rounded-xl border p-3 shadow-sm"
                  >
                    {showCommentActions && (
                      <div className="absolute top-2 right-2 flex gap-1">
                        {canEditComment && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => {
                              handleStartEditComment(comment);
                            }}
                            aria-label="Edit comment"
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                        )}
                        {canDeleteComment && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="text-destructive hover:text-destructive h-8 w-8"
                            onClick={() => {
                              setCommentToDelete(comment);
                            }}
                            aria-label="Delete comment"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    )}
                    <UserProfilePhoto
                      user={{
                        id: commentUser?.id ?? "",
                        firstName: commentUser?.firstName ?? "",
                        lastName: commentUser?.lastName ?? "",
                        profilePicturePresignedUrlKey:
                          commentUser?.profilePicturePresignedUrlKey,
                      }}
                      className="h-8 w-8"
                    />
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-x-2 pr-12">
                        <Text variant="body" className="font-semibold">
                          {formatUsersName({
                            firstName: commentUser?.firstName ?? "",
                            lastName: commentUser?.lastName ?? "",
                          }) ?? "Unknown User"}
                        </Text>
                        <Text variant="caption" color="muted" as="span">
                          •
                        </Text>
                        <Text variant="caption" color="muted" as="span">
                          {comment.dateCreated
                            ? formatDateTime(
                                convertToLocalFromDatabaseDateTime(
                                  comment.dateCreated,
                                ),
                                false,
                              )
                            : ""}
                        </Text>
                      </div>
                      {isEditingComment ? (
                        <div className="border-border bg-card mt-2 rounded-xl border shadow-sm">
                          <EditorContent
                            editor={editCommentEditor}
                            className="max-h-48 overflow-y-auto"
                            onKeyDown={(event) => {
                              if (
                                event.key !== "Enter" ||
                                event.shiftKey ||
                                event.altKey ||
                                event.metaKey ||
                                event.ctrlKey
                              ) {
                                return;
                              }

                              if (
                                editCommentMentionDropdownOpenRef.current ||
                                !canSaveEditedComment
                              ) {
                                return;
                              }

                              event.preventDefault();
                              event.stopPropagation();
                              void handleSaveComment();
                            }}
                          />
                          <div className="flex items-center justify-end gap-2 border-t border-slate-100 px-4 py-2.5">
                            <Button
                              type="button"
                              variant="outline"
                              onClick={handleCancelEditComment}
                              disabled={updateMatterNoteComment.isPending}
                            >
                              Cancel
                            </Button>
                            <Button
                              type="button"
                              onClick={() => {
                                void handleSaveComment();
                              }}
                              disabled={!canSaveEditedComment}
                            >
                              Update
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div className="mt-2">
                          <MatterNoteCommentText
                            text={comment.message}
                            mentionedApps={comment.mentionedApps}
                            users={users}
                          />
                        </div>
                      )}
                    </div>
                  </article>
                );
              })}
            </div>
          )}
          {canCreateComment && (
            <div className="border-border bg-card overflow-visible rounded-xl border shadow-sm">
              <EditorContent
                editor={commentEditor}
                className="max-h-48 overflow-y-auto"
                onKeyDown={(event) => {
                  if (
                    event.key !== "Enter" ||
                    event.shiftKey ||
                    event.altKey ||
                    event.metaKey ||
                    event.ctrlKey
                  ) {
                    return;
                  }

                  if (commentMentionDropdownOpenRef.current) {
                    return;
                  }

                  if (!canSubmitComment) {
                    return;
                  }

                  event.preventDefault();
                  event.stopPropagation();
                  void handleAddComment();
                }}
              />
              <div className="flex items-center justify-end gap-2 px-4 py-2.5">
                <Button
                  type="button"
                  onClick={() => {
                    void handleAddComment();
                  }}
                  disabled={!canSubmitComment}
                >
                  Add comment
                </Button>
              </div>
            </div>
          )}
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  ) : null;

  const deleteCommentModal = shouldRenderComments ? (
    <ActionConfirmationModal
      title={`Are you sure you want to delete this comment?`}
      open={Boolean(commentToDelete)}
      setOpen={(open) => {
        if (!open) {
          setCommentToDelete(null);
        }
      }}
      afterActionFn={() => {
        void handleDeleteComment();
      }}
      isActionPending={deleteMatterNoteComment.isPending}
    />
  ) : null;

  if (variant === "eventlog") {
    const showActionsDropdown = !isEditing && (canEditNote || canDeleteNote);
    const eventLogCardClassName = cn(
      "relative bg-card border-border/50 flex items-start space-x-3 overflow-visible rounded-lg border p-3",
      note.pinned && highlightPinned && "bg-primary/5",
      isEditing && "flex-col gap-4",
    );

    return (
      <>
        <MatterNoteContextMenu
          isEditing={isEditing}
          canEdit={canEditNote}
          canDelete={canDeleteNote}
          copyValue={displayMessage}
          creationInfo={creationInfo}
          isDraft={Boolean(note.draft)}
          isPinned={Boolean(note.pinned)}
          isUpdating={updateMatterNote.isPending}
          massTortId={massTortId}
          matterId={matter.id}
          noteId={note.id}
          onDraftToggle={() => {
            void handleDraftToggle();
          }}
          onEdit={handleStartEdit}
          onDelete={() => {
            setIsDeleting(true);
          }}
          onPinToggle={() => {
            void handlePinToggle();
          }}
          trigger={
            <div className={eventLogCardClassName}>
              {showActionsDropdown && (
                <div className="absolute top-2 right-2">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 shrink-0"
                        data-testid="note-actions-dropdown-trigger"
                      >
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      {canEditNote && (
                        <DropdownMenuItem
                          onClick={() => {
                            void handlePinToggle();
                          }}
                          disabled={updateMatterNote.isPending}
                        >
                          {Boolean(note.pinned) ? (
                            <>
                              <PinOff className="mr-2 h-4 w-4" /> Unpin
                            </>
                          ) : (
                            <>
                              <Pin className="mr-2 h-4 w-4" /> Pin
                            </>
                          )}
                        </DropdownMenuItem>
                      )}
                      {canEditNote && (
                        <DropdownMenuItem
                          onClick={handleStartEdit}
                          data-testid="edit-note-dropdown-item"
                        >
                          <Pencil className="mr-2 h-4 w-4" /> Edit
                        </DropdownMenuItem>
                      )}
                      {canDeleteNote && (
                        <DropdownMenuItem
                          onClick={() => {
                            setIsDeleting(true);
                          }}
                          data-testid="delete-note-dropdown-item"
                        >
                          <Trash2 className="mr-2 h-4 w-4" /> Delete
                        </DropdownMenuItem>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              )}
              <div className={cn("min-w-0 flex-1", isEditing && "w-full")}>
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2">
                    <MatterBreadcrumb items={breadcrumbItems} />
                    {isDraftNote && (
                      <Badge variant="outline" className="h-6 shrink-0">
                        Draft
                      </Badge>
                    )}
                  </div>
                </div>
                {isEditing ? (
                  <div
                    className={cn(
                      "border-border/80 my-3 w-full rounded-md border",
                      note.pinned && highlightPinned
                        ? "bg-transparent"
                        : "bg-card",
                    )}
                  >
                    <EditorContent editor={editor} className="w-full" />
                    <div className="border-border/60 flex flex-wrap items-center justify-between gap-2 border-t px-4 py-2.5">
                      {note.draft && (
                        <label
                          htmlFor={draftCheckboxId}
                          className="flex cursor-pointer items-center gap-2"
                        >
                          <Checkbox
                            id={draftCheckboxId}
                            checked={isDraft}
                            disabled={updateMatterNote.isPending}
                            onCheckedChange={(checked) => {
                              setIsDraft(checked === true);
                            }}
                          />
                          <Text variant="body" color="muted">
                            Draft
                          </Text>
                        </label>
                      )}
                      <div className="ml-auto flex items-center gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={handleCancelEdit}
                          disabled={updateMatterNote.isPending}
                        >
                          Cancel
                        </Button>
                        <Button
                          type="button"
                          onClick={() => {
                            handleSave();
                          }}
                          disabled={!canSave}
                        >
                          Update
                        </Button>
                      </div>
                    </div>
                  </div>
                ) : (
                  shouldShowMessage && (
                    <div className="mt-1 flex flex-col gap-2">
                      <Text
                        ref={textContentRef}
                        variant="subHeading"
                        as="div"
                        className={cn(
                          "whitespace-pre-wrap",
                          !isExpanded && "line-clamp-4",
                          textClassName,
                        )}
                      >
                        <TextWithMentions
                          text={note.message}
                          mentionedApps={note.mentionedApps}
                          users={users}
                          className="cursor-text [&_a]:cursor-pointer"
                        />
                      </Text>
                      {hasOverflow && (
                        <button
                          type="button"
                          className="w-fit"
                          onClick={() => {
                            setIsExpanded((prev) => !prev);
                          }}
                        >
                          <Text
                            variant="caption"
                            color="primary"
                            className="hover:underline"
                          >
                            {isExpanded ? "Show less" : "Show more"}
                          </Text>
                        </button>
                      )}
                    </div>
                  )
                )}
                {!isEditing && (
                  <CreatedByUserText
                    user={noteAuthor}
                    dateTime={note.publishedDate}
                    showTime
                  />
                )}
                {commentsSection}
              </div>
            </div>
          }
        />
        {deleteCommentModal}
        <ActionConfirmationModal
          title={`Are you sure you want to delete this note?`}
          open={isDeleting}
          setOpen={setIsDeleting}
          afterActionFn={() => {
            void handleDelete();
          }}
          isActionPending={deleteMatterNote.isPending}
        />
      </>
    );
  }

  const cardClassName = cn(
    "relative flex h-fit overflow-visible border-none p-4",
    note.pinned && highlightPinned && "bg-primary/5",
    isEditing ? "flex-col gap-4" : "justify-between",
  );

  return (
    <>
      <Card key={note.id} className={cardClassName}>
        <div className="flex flex-1 flex-col gap-1 space-y-1">
          <MatterNoteContextMenu
            isEditing={isEditing}
            canEdit={canEditNote}
            canDelete={canDeleteNote}
            copyValue={displayMessage}
            creationInfo={creationInfo}
            isDraft={Boolean(note.draft)}
            isPinned={Boolean(note.pinned)}
            isUpdating={updateMatterNote.isPending}
            massTortId={massTortId}
            matterId={matter.id}
            noteId={note.id}
            onDraftToggle={() => {
              void handleDraftToggle();
            }}
            onEdit={handleStartEdit}
            onDelete={() => {
              setIsDeleting(true);
            }}
            onPinToggle={() => {
              void handlePinToggle();
            }}
            trigger={
              <div
                className={cn(
                  !isEditing && "-mx-4 -mt-4 rounded-t-lg px-4 pt-4 pb-2",
                )}
              >
                {!isEditing && (
                  <div className="absolute top-4 right-4 flex gap-2">
                    {canEditNote && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={handleStartEdit}
                        aria-label="Edit note"
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                    )}
                    {canEditNote && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => {
                          void handlePinToggle();
                        }}
                        disabled={updateMatterNote.isPending}
                        aria-label={note.pinned ? "Unpin note" : "Pin note"}
                      >
                        {note.pinned ? (
                          <PinOff className="h-4 w-4" />
                        ) : (
                          <Pin className="h-4 w-4" />
                        )}
                      </Button>
                    )}
                    {canDeleteNote && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="text-destructive hover:text-destructive h-8 w-8"
                        onClick={() => {
                          setIsDeleting(true);
                        }}
                        aria-label="Delete note"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                )}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {isPinned && <Pin className="text-primary h-4 w-4" />}
                    <MatterBreadcrumb items={breadcrumbItems} />
                    {isDraftNote && (
                      <Badge variant="outline" className="h-6 shrink-0">
                        Draft
                      </Badge>
                    )}
                  </div>
                </div>
                {isEditing ? (
                  <div
                    className={cn(
                      "border-border/80 my-3 w-full rounded-md border",
                      note.pinned && highlightPinned
                        ? "bg-transparent"
                        : "bg-card",
                    )}
                  >
                    <EditorContent editor={editor} className="w-full" />
                    <div className="border-border/60 flex flex-wrap items-center justify-between gap-2 border-t px-4 py-2.5">
                      {note.draft && (
                        <label
                          htmlFor={draftCheckboxId}
                          className="flex cursor-pointer items-center gap-2"
                        >
                          <Checkbox
                            id={draftCheckboxId}
                            checked={isDraft}
                            disabled={updateMatterNote.isPending}
                            onCheckedChange={(checked) => {
                              setIsDraft(checked === true);
                            }}
                          />
                          <Text variant="body" color="muted">
                            Draft
                          </Text>
                        </label>
                      )}
                      <div className="ml-auto flex items-center gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={handleCancelEdit}
                          disabled={updateMatterNote.isPending}
                        >
                          Cancel
                        </Button>
                        <Button
                          type="button"
                          onClick={() => {
                            handleSave();
                          }}
                          disabled={!canSave}
                        >
                          Update
                        </Button>
                      </div>
                    </div>
                  </div>
                ) : (
                  shouldShowMessage && (
                    <div className="flex flex-col gap-2">
                      <div
                        ref={textContentRef}
                        className={cn(
                          !isExpanded && "line-clamp-4",
                          textClassName,
                        )}
                      >
                        <Text
                          variant="body"
                          as="div"
                          className="whitespace-pre-wrap"
                        >
                          <TextWithMentions
                            text={note.message}
                            mentionedApps={note.mentionedApps}
                            users={users}
                            className="cursor-text [&_a]:cursor-pointer"
                          />
                        </Text>
                      </div>
                      {hasOverflow && (
                        <button
                          type="button"
                          className="w-fit"
                          onClick={() => {
                            setIsExpanded((prev) => !prev);
                          }}
                        >
                          <Text
                            variant="body"
                            color="primary"
                            className="hover:underline"
                          >
                            {isExpanded ? "Show less" : "Show more"}
                          </Text>
                        </button>
                      )}
                    </div>
                  )
                )}
                <div className="flex flex-wrap items-center gap-2">
                  <Text
                    variant="subHeading"
                    className="flex items-center gap-x-2"
                  >
                    <UserProfilePhoto
                      user={{
                        id: noteAuthor?.id ?? "",
                        firstName: noteAuthor?.firstName ?? "",
                        lastName: noteAuthor?.lastName ?? "",
                        profilePicturePresignedUrlKey:
                          noteUser?.profilePicturePresignedUrlKey,
                      }}
                      className="h-4 w-4"
                      initialsClassName="text-[8px]"
                    />
                    {noteAuthorLabel}
                  </Text>
                  <Text variant="subHeading" as="span">
                    •
                  </Text>
                  <Text variant="subHeading" as="span">
                    {formatDateTime(
                      convertToLocalFromDatabaseDateTime(note.publishedDate),
                      false,
                    )}
                  </Text>
                </div>
              </div>
            }
          />
          {commentsSection}
        </div>
      </Card>
      {deleteCommentModal}
      <ActionConfirmationModal
        title={`Are you sure you want to delete this note?`}
        open={isDeleting}
        setOpen={setIsDeleting}
        afterActionFn={() => {
          void handleDelete();
        }}
        isActionPending={deleteMatterNote.isPending}
      />
    </>
  );
};
